// src/components/UnsplashCredits/UnsplashCredits.tsx
import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { isUnsplashWithoutAuthor, withUnsplashUtm } from '../../utils/unsplashAttribution';
import classes from './UnsplashCredits.module.less';

// Pusty kontener w DefaultLayout, tuż nad stopką — tam trafia podpis
export const CREDITS_HOST_ID = 'unsplash-credits';

interface Author {
  name: string;
  url?: string;
}

interface UnsplashCreditsProps {
  // Słowa, których zdjęcia są w tej chwili na ekranie
  words?: any[];
}

// Wytyczne Unsplash API wymagają podpisu tam, gdzie zdjęcie jest pokazywane. Widok mówi,
// czyje zdjęcia ma teraz na ekranie, a sam podpis renderuje się przez portal jako pasek nad
// stopką — dzięki temu nie rozpycha układu gier i zawsze stoi w tym samym miejscu.
// Własne uploady nic nie dokładają. Zdjęcia z Unsplasha bez znanego autora (sprzed września)
// też muszą wskazać źródło, więc sam ich widok wystarcza, żeby pasek się pokazał.
export const UnsplashCredits: FC<UnsplashCreditsProps> = ({ words }) => {
  const { t } = useTranslation();
  const [host, setHost] = useState<HTMLElement | null>(null);

  // Kontener layoutu istnieje w DOM dopiero po zamontowaniu, stąd odczyt w efekcie
  useEffect(() => {
    setHost(document.getElementById(CREDITS_HOST_ID));
  }, []);

  const authors: Author[] = [];
  let hasUnknownAuthor = false;

  (words || []).forEach((word) => {
    const meta = word?.imageMeta;

    if (isUnsplashWithoutAuthor(word)) {
      hasUnknownAuthor = true;

      return;
    }

    if (!word?.image || meta?.source !== 'unsplash' || !meta?.authorName) {
      return;
    }

    if (!authors.some((author) => author.name === meta.authorName)) {
      authors.push({ name: meta.authorName, url: meta.authorUrl });
    }
  });

  if (!host || (!authors.length && !hasUnknownAuthor)) {
    return null;
  }

  const unsplashLink = (
    <a href={withUnsplashUtm('https://unsplash.com/')} target="_blank" rel="noreferrer">
      Unsplash
    </a>
  );

  // Same zdjęcia bez autora: podpis ogólny, bo nazwiska nie ma czym wypełnić
  if (!authors.length) {
    return createPortal(
      <div className={classes.creditsBar}>
        {t('words-list:credits.photosFrom')} {unsplashLink}
      </div>,
      host,
    );
  }

  return createPortal(
    <div className={classes.creditsBar}>
      {t('words-list:credits.photos')}{' '}
      {authors.map((author, index) => (
        <React.Fragment key={author.name}>
          {index > 0 && ', '}
          {author.url ? (
            <a href={withUnsplashUtm(author.url)} target="_blank" rel="noreferrer">
              {author.name}
            </a>
          ) : author.name}
        </React.Fragment>
      ))}
      {/* Na ekranie są też zdjęcia, których autora nie znamy — nie przypisujemy ich wymienionym */}
      {hasUnknownAuthor && t('words-list:credits.andOthers')}
      {' / '}
      {unsplashLink}
    </div>,
    host,
  );
};

export default UnsplashCredits;
